import { useEffect } from 'react'

import { nanoid } from 'nanoid'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'

import { usePrevious } from '@bettermode/common/hooks'
import { VirtualLocation } from '@tribeplatform/gql-client/types'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { Form } from '../../Form/index.js'
import { T } from '../../i18n/index.js'
import { VirtualLinkProvider } from '../types.js'

import { detectProvider } from './utils.js'
import { VirtualLinkIcon } from './VirtualLinkIcon.js'

const VirtualLocationProviderPicker = ({ index }: { index: number }) => {
  const { control, setValue } = useFormContext()
  const url = useWatch({
    control,
    name: `virtualLocations.${index}.url`,
  })
  const provider = useWatch({
    control,
    name: `virtualLocations.${index}.provider`,
  }) as VirtualLinkProvider
  const previousValue = usePrevious(url)

  useEffect(() => {
    if (provider && provider !== VirtualLinkProvider.Custom) {
      return
    }

    if (url && previousValue !== url) {
      const detected = detectProvider(url)
      if (detected && detected !== provider) {
        setValue(`virtualLocations.${index}.provider`, detected, {
          shouldDirty: true,
        })
      }
    }
  }, [previousValue, url, provider, index, setValue])

  return (
    <Dropdown>
      <Dropdown.ButtonMinimal>
        <div className="flex items-center gap-x-2 border-e pe-2">
          <VirtualLinkIcon provider={provider} className="size-6" />
          <SvgIcon name="chevron-down" />
        </div>
      </Dropdown.ButtonMinimal>
      <Dropdown.Items maxHeight="sm">
        {Object.values(VirtualLinkProvider).map(provider => {
          return (
            <Dropdown.Item
              key={provider}
              onClick={() =>
                setValue(`virtualLocations.${index}.provider`, provider, {
                  shouldDirty: true,
                })
              }
            >
              <div className="flex items-center gap-x-2">
                <VirtualLinkIcon provider={provider} className="size-6" />
                {provider}
              </div>
            </Dropdown.Item>
          )
        })}
      </Dropdown.Items>
    </Dropdown>
  )
}

export const VirtualLocationsPicker = () => {
  const { fields, append, remove } = useFieldArray<{
    virtualLocations: VirtualLocation[]
  }>({
    name: 'virtualLocations',
  })

  return (
    <div>
      <div className="flex flex-col gap-2">
        {fields.map((field, index) => {
          return (
            <Form.Input
              key={field.id}
              name={`virtualLocations.${index}.url`}
              type="url"
              label={
                index === 0 ? (
                  <T
                    defaultMessage="Virtual event links"
                    id="Event.Form.VirtualLocation.Title"
                  />
                ) : null
              }
              leadingAddon={<VirtualLocationProviderPicker index={index} />}
              trailingIcon={
                <Button
                  size="md"
                  onClick={() => remove(index)}
                  variant="tertiaryNeutral"
                  leadingIcon={<SvgIcon name="trash" />}
                />
              }
            />
          )
        })}
      </div>
      <Button
        className="mt-3"
        onClick={() =>
          append({
            url: '',
            provider: VirtualLinkProvider.Custom,
            id: nanoid(),
          })
        }
        variant="tertiaryNeutral"
        leadingIcon={<SvgIcon name="plus" />}
      >
        {fields.length > 0 ? (
          <T
            defaultMessage="Add another virtual event link"
            id="Event.Form.VirtualLocation.AddAnother"
          />
        ) : (
          <T
            defaultMessage="Add a virtual event link"
            id="Event.Form.VirtualLocation.AddVirtualLink"
          />
        )}
      </Button>
    </div>
  )
}
